import { FunnelStatus } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Input from '@/components/basicComponents/input';
import { SORT_ORDER } from '@/components/table/types';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { CREATE_DUE_DILIGENCE_FUNNEL_MUTATION, DUE_DILIGENCE_FUNNEL_LIST_SETTINGS_QUERY } from '../queries';
import { IFunnelDataWithStatuses } from '../types';
import { saveOnEnter } from '../utils';

interface AddFunnelProps {
  isOpen: boolean;
  currentFunnel: IFunnelDataWithStatuses | null;
  openFunnelEditor: (funnelId: string, funnelName: string) => void;
  onClose: () => void;
}

const AddFunnel = ({ isOpen, currentFunnel, openFunnelEditor, onClose }: AddFunnelProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState('');

  const [createFunnel] = useMutation(CREATE_DUE_DILIGENCE_FUNNEL_MUTATION, {
    refetchQueries: () => [
      {
        query: DUE_DILIGENCE_FUNNEL_LIST_SETTINGS_QUERY,
        variables: {
          data: {
            statusFilter: ['PUBLISHED', 'DRAFT_WITHOUT_PUBLISHED'],
            sortDirection: SORT_ORDER.ASC,
            sortBy: 'ORDER'
          },
          archivedFunnelData: {
            sortDirection: SORT_ORDER.ASC,
            sortBy: 'ORDER'
          }
        }
      }
    ]
  });

  const saveValue = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      const { data } = await createFunnel({
        variables: {
          data: {
            name: value,
            funnelStaticId: currentFunnel ? currentFunnel.funnelStaticId : crypto.randomUUID(),
            externalId: 'test-3',
            status: FunnelStatus.Draft,
            tiers: []
          }
        }
      });

      const { id, name } = data.createDueDiligenceFunnel;
      openFunnelEditor(id, name);
      onClose();
    } catch (error) {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalWrappedContent
      title="Add New Funnel"
      isOpen={isOpen}
      onClose={onClose}
      showCloseIcon={!loading}
      showCancelButton={!loading}
      isUseOutsideClick={!loading}
      showRequiredFields={!loading}
      confirmButton={{ name: 'Save', onClick: saveValue, disabled: !value || loading }}
    >
      {loading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <ErrorBannerWrapper>
              <Banner
                icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                title="Error"
                description={errorMessage}
                bgColor={themeVar('context.error')}
              />
            </ErrorBannerWrapper>
          )}
          <Input
            label="Give this funnel a name"
            value={value}
            onChange={(event) => setValue(event.target.value)}
            placeholder="Give this Funnel a unique name"
            size="lg"
            onKeyUp={(event) => saveOnEnter(event, saveValue, !value)}
            autoFocus
            isRequiredField
          />
        </>
      )}
    </ModalWrappedContent>
  );
};

export default AddFunnel;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
